import {useCallback, useEffect, useRef, useState} from "react";
import {ApiError, formatMerchantApiError} from "../api";
import {useAuth} from "../auth";

const DEFAULT_POLL_INTERVAL_MS = 30_000;

type RequestReason = "initial" | "manual" | "poll" | "resume";

export interface MerchantQueryState<T> {
  data: T | null;
  loading: boolean;
  refreshing: boolean;
  error: string | null;
  stale: boolean;
  lastSuccessfulAt: Date | null;
  refresh: () => void;
}

export interface MerchantQueryOptions {
  enabled?: boolean;
  pollIntervalMs?: number;
  formatError?: (error: unknown) => string;
}

/**
 * Shared lifecycle for merchant read requests.
 *
 * A failed refresh keeps the last successful value, but pauses automatic
 * polling until an explicit retry succeeds. Poll ticks never start a parallel
 * request; manual refresh cancels the obsolete request with AbortController.
 */
export function useMerchantQuery<T>(
  request: (signal: AbortSignal) => Promise<T>,
  queryKey: string,
  options: MerchantQueryOptions = {},
): MerchantQueryState<T> {
  const {
    enabled = true,
    pollIntervalMs = DEFAULT_POLL_INTERVAL_MS,
    formatError = formatMerchantApiError,
  } = options;
  const {logout} = useAuth();

  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(enabled);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastSuccessfulAt, setLastSuccessfulAt] = useState<Date | null>(null);

  const dataRef = useRef<T | null>(null);
  const requestRef = useRef(request);
  const logoutRef = useRef(logout);
  const formatErrorRef = useRef(formatError);
  const activeControllerRef = useRef<AbortController | null>(null);
  const automaticPollingPausedRef = useRef(false);
  const executeRef = useRef<((reason: RequestReason) => void) | null>(null);

  requestRef.current = request;
  logoutRef.current = logout;
  formatErrorRef.current = formatError;

  useEffect(() => {
    let disposed = false;

    activeControllerRef.current?.abort();
    activeControllerRef.current = null;
    automaticPollingPausedRef.current = false;
    dataRef.current = null;
    setData(null);
    setError(null);
    setLastSuccessfulAt(null);
    setRefreshing(false);
    setLoading(enabled);

    if (!enabled) {
      executeRef.current = null;
      return;
    }

    const execute = (reason: RequestReason): void => {
      if (disposed) return;
      const automatic = reason === "poll" || reason === "resume";
      if (automatic && automaticPollingPausedRef.current) return;
      if (automatic && activeControllerRef.current) return;

      activeControllerRef.current?.abort();
      const controller = new AbortController();
      activeControllerRef.current = controller;

      if (reason === "manual") {
        automaticPollingPausedRef.current = false;
        setError(null);
      }
      if (dataRef.current === null) {
        setLoading(true);
      } else {
        setRefreshing(true);
      }

      void requestRef.current(controller.signal)
        .then((response) => {
          if (disposed || controller.signal.aborted) return;
          dataRef.current = response;
          setData(response);
          setError(null);
          setLastSuccessfulAt(new Date());
          automaticPollingPausedRef.current = false;
        })
        .catch((requestError: unknown) => {
          if (disposed || controller.signal.aborted) return;
          if (requestError instanceof ApiError && requestError.status === 401) {
            automaticPollingPausedRef.current = true;
            dataRef.current = null;
            setData(null);
            setError(null);
            logoutRef.current();
            return;
          }
          automaticPollingPausedRef.current = true;
          setError(formatErrorRef.current(requestError));
        })
        .finally(() => {
          if (disposed || activeControllerRef.current !== controller) return;
          activeControllerRef.current = null;
          setLoading(false);
          setRefreshing(false);
        });
    };

    executeRef.current = execute;
    execute("initial");

    const interval = window.setInterval(() => {
      if (document.visibilityState === "visible") execute("poll");
    }, pollIntervalMs);

    const handleVisibilityChange = (): void => {
      if (document.visibilityState === "visible") execute("resume");
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      disposed = true;
      window.clearInterval(interval);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      activeControllerRef.current?.abort();
      activeControllerRef.current = null;
      executeRef.current = null;
    };
  }, [enabled, pollIntervalMs, queryKey]);

  const refresh = useCallback((): void => {
    executeRef.current?.("manual");
  }, []);

  return {
    data,
    loading,
    refreshing,
    error,
    stale: data !== null && error !== null,
    lastSuccessfulAt,
    refresh,
  };
}
